import { Box, HBox, VBox } from "@ui/box/Box";
import { Avatar as KitAvatar, DS } from "./ds/Kit";
import { Typo } from "@ui/text/Typo";
import { GD } from "src/GD";
import { Avatar } from "./Avatar";
import { IconArrowDown } from "@ui/icons/IconArrowDown";
import { useEffect, useRef, useState } from "react";
import { CompanyDetails } from "./components/CompanyDetails";
import { S_UNIVERSAL_DIALOG } from "./components/universalDialog/signals";
import { USER_ROLE } from "src/services/AuthService";
import { ProfileSettings } from "./components/ProfileSettings";
import { LogoMark } from "./assets/Logo";
import { IssuerSwitcher } from "./issuer/IssuerSwitcher";
import { useIssuer } from "./issuer/IssuerProvider";
import Button from "@ui/button/Button";
import { PrivateFunding } from "./private/pages/PrivateFunding";
import { getIssuerIdentity } from "./issuer/issuerIdentity";
import { registerSystemBackOverlay } from "@ui/utils/systemBack";
import { MOBILE_VIEWPORT_MEDIA as MOBILE } from "@ui/utils/mobileViewport";
import { useCurrentPath } from "src/services/Router";

const HEADER_BRAND = "#27445D";
const HEADER_TEXT = "#1F2937";
const HEADER_MUTED = "#6B7280";
const HEADER_BORDER = "#F3F4F6";
const HEADER_SOFT = "#F8FAFC";

export const MobileHeaderBrand = ({ role }: { role: string }) => (
  <Box
    sx={{
      display: "none",
      flexShrink: 0,
      [MOBILE]: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
      },
    }}
  >
    <LogoMark size="40px" />
    <VBox
      gap="2px"
      sx={{
        minWidth: 0,
        "@media (max-width: 480px)": { display: "none" },
      }}
    >
      <Typo
        sx={{
          color: HEADER_TEXT,
          fontSize: "17px",
          lineHeight: 1.05,
          fontWeight: 800,
          letterSpacing: "-0.01em",
          whiteSpace: "nowrap",
        }}
      >
        Cardpartner
      </Typo>
      <Typo
        sx={{
          color: HEADER_MUTED,
          fontSize: "10.5px",
          lineHeight: 1.1,
          textTransform: "capitalize",
          whiteSpace: "nowrap",
        }}
      >
        {role} workspace
      </Typo>
    </VBox>
  </Box>
);

export const Header = () => {
  const currentPath = useCurrentPath();
  const [profile, , setProfile] = GD.REQ_PROFILE.useRequest();
  const { selectedIssuer } = useIssuer();
  const issuerIdentity = getIssuerIdentity(selectedIssuer?.issuer);
  const [menuOpen, setMenuOpen] = useState(false);
  const setProfileRef = useRef(setProfile);
  setProfileRef.current = setProfile;

  useEffect(() => {
    const subscription = GD.S_PROFILE_UPDATED.subscribe(() => {
      void GD.REQ_PROFILE.request().then((nextProfile) =>
        setProfileRef.current(nextProfile),
      );
    });
    return () => {
      GD.S_PROFILE_UPDATED.unsubscribe(subscription);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const close = () => {
      setMenuOpen(false);
      document.querySelector<HTMLElement>('[aria-label="Open account menu"]')?.focus();
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      close();
    };
    window.addEventListener("keydown", handleKey);
    const frame = requestAnimationFrame(() => document.querySelector<HTMLElement>('[data-profile-menu] button')?.focus());
    const unregister = registerSystemBackOverlay(close);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("keydown", handleKey);
      unregister();
    };
  }, [menuOpen]);

  const openCompanySettings = async () => {
    const { data: companyData } = await GD.REQ_COMPANIES.request({});
    if (!companyData) return;

    S_UNIVERSAL_DIALOG.invoke({
      label: "Company settings",
      size: "medium",
      contentPadding: "0px",
      content: (
        <CompanyDetails
          company={companyData[0]} // Assuming single company for now
          onUpdate={async () => {
            await GD.REQ_COMPANIES.request({ force: true });
          }}
        />
      ),
      closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
    });
  };

  if (!profile) return null;

  return (
    <HBox
      data-active-issuer={selectedIssuer?.issuer ?? "none"}
      sx={{
        height:
          "calc(64px + var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))",
        backgroundColor: "#fff",
        borderBottom: `1px solid ${HEADER_BORDER}`,
        boxShadow: selectedIssuer
          ? `inset 0 -1px 0 ${issuerIdentity.accent}52, inset 0 -3px 6px ${issuerIdentity.accent}1F, 0 1px 5px ${issuerIdentity.accent}14`
          : "none",
        transition: "box-shadow .2s ease",
        alignItems: "center",
        padding:
          "var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) 28px 0",
        justifyContent: "start",
        [MOBILE]: {
          gap: "12px",
          height:
            "calc(70px + var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))",
          padding:
            "var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) 14px 0",
        },
      }}
    >
      <MobileHeaderBrand role={profile.company_role} />
      <VBox gap="3px" sx={{ minWidth: 0, [MOBILE]: { display: "none" } }}>
        <Typo sx={{ fontSize: "10px", letterSpacing: ".08em", textTransform: "uppercase", color: "#617087" }}>Corporate workspace</Typo>
        <Typo sx={{ color: "#17243B", fontWeight: 650, fontSize: "14px" }}>{currentPath?.currentNav?.label || "Overview"}</Typo>
      </VBox>
      <Box
        sx={{
          position: "relative",
          flex: "1 1 auto",
          width: "auto",
          minWidth: 0,
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
        }}
      >
        <HBox
          gap="10px"
          sx={{
            alignItems: "center",
            marginRight: "14px",
            [MOBILE]: { marginRight: "6px" },
          }}
        >
          <Box sx={{ [MOBILE]: { display: "none" } }}>
            <IssuerSwitcher compact={false} />
          </Box>
          <Box sx={{ display: "none", [MOBILE]: { display: "block" } }}>
            <IssuerSwitcher compact />
          </Box>
          {selectedIssuer?.crypto_topup && (
            <Button
              title="Add funds"
              variant="secondarySmall"
              onClick={() => {
                S_UNIVERSAL_DIALOG.invoke({
                  label: `Add funds · ${selectedIssuer.name}`,
                  size: "large",
                  content: <PrivateFunding />,
                  closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
                });
              }}
              sx={{ [MOBILE]: { display: "none" } }}
            />
          )}
        </HBox>
        <HBox
          element="button"
          type="button"
          aria-label="Open account menu"
          aria-expanded={menuOpen}
          sx={{
            fontFamily: "inherit", backgroundColor: "transparent",
            alignItems: "center",
            cursor: "pointer",
            padding: "6px 10px",
            borderRadius: "10px",
            border: "1px solid transparent",
            transition: "background-color 0.2s ease, border-color 0.2s ease",
            "&:hover": { backgroundColor: HEADER_SOFT, borderColor: "#E5E7EB" },
            [MOBILE]: {
              padding: "5px 4px 5px 8px",
              gap: "6px",
            },
          }}
          gap="12px"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <VBox
            sx={{
              alignItems: "flex-end",
              "@media (max-width: 480px)": { display: "none" },
            }}
          >
            <Typo
              variant="medium"
              sx={{ color: HEADER_TEXT, fontWeight: 700, lineHeight: "1.2" }}
            >
              {profile.name || profile.email}
            </Typo>
            <Typo
              variant="caption"
              sx={{
                color: HEADER_MUTED,
                fontSize: "11px",
                textTransform: "uppercase",
              }}
            >
              {profile.company_role}
            </Typo>
          </VBox>
          <Avatar size="32px" />
          <Box sx={{ color: HEADER_BRAND, lineHeight: "0px" }}>
            <IconArrowDown width="20" height="20" />
          </Box>
        </HBox>

        {menuOpen && (
          <>
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 99,
              }}
              onClick={() => setMenuOpen(false)}
            />
            <VBox
              data-profile-menu
              role="group"
              aria-label="Account actions"
              sx={{
                position: "absolute",
                top: "100%",
                right: 0,
                marginTop: "10px",
                backgroundColor: "#FFFFFF",
                borderRadius: "16px",
                boxShadow: "0 20px 44px rgba(15, 23, 42, 0.16), 0 2px 6px rgba(15, 23, 42, 0.06)",
                border: `1px solid ${DS.line}`,
                width: "264px",
                zIndex: 100,
                overflow: "hidden",
                padding: "6px",
              }}
            >
              <HBox gap="12px" sx={{ alignItems: "center", padding: "12px 10px 14px", marginBottom: "4px", borderBottom: `1px solid ${DS.line}` }}>
                <KitAvatar name={profile.name} size={40} />
                <VBox gap="2px" sx={{ minWidth: 0 }}>
                  <Typo sx={{ color: DS.ink, fontWeight: 650, fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{profile.name}</Typo>
                  <Typo sx={{ color: DS.muted, fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{profile.email || String(profile.company_role ?? "")}</Typo>
                </VBox>
              </HBox>
              {[
                {
                  label: "View profile",
                  path: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
                  show: true,
                  onClick: () => S_UNIVERSAL_DIALOG.invoke({
                    label: "Profile settings",
                    size: "medium",
                    content: <ProfileSettings />,
                    closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
                  }),
                },
                {
                  label: "Company settings",
                  path: <><path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 21v-6h6v6" /></>,
                  show: profile.company_role === USER_ROLE.ADMIN,
                  onClick: openCompanySettings,
                },
              ].filter(item => item.show).map(item => (
                <Box
                  key={item.label}
                  element="button"
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    item.onClick();
                  }}
                  sx={{
                    width: "100%", minHeight: "42px", border: 0, background: "transparent", font: "inherit", textAlign: "left",
                    display: "flex", alignItems: "center", gap: "10px", padding: "0 10px", borderRadius: "10px", cursor: "pointer",
                    color: DS.ink, fontSize: "14px", fontWeight: 500,
                    "& svg": { color: DS.muted },
                    "&:hover": { backgroundColor: DS.soft },
                    "&:hover svg": { color: DS.accent },
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{item.path}</svg>
                  {item.label}
                </Box>
              ))}
              <Box sx={{ height: "1px", background: DS.line, margin: "4px 0" }} />
              <Box
                element="button"
                type="button"
                onClick={() => {
                  GD.S_AUTH_LOG_OUT.invoke();
                  setMenuOpen(false);
                }}
                sx={{
                  width: "100%", minHeight: "42px", border: 0, background: "transparent", font: "inherit", textAlign: "left",
                  display: "flex", alignItems: "center", gap: "10px", padding: "0 10px", borderRadius: "10px", cursor: "pointer",
                  color: "#B42318", fontSize: "14px", fontWeight: 600,
                  "&:hover": { backgroundColor: "#FEF2F2" },
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></svg>
                Log out
              </Box>
            </VBox>
          </>
        )}
      </Box>
    </HBox>
  );
};
